import unionBy from 'lodash/unionBy';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import type {
  BrandBusinessHours,
  BrandBusinessHoursInput,
} from '@/app/settings/types/business-hours';

import * as AuthAPI from '@/apis/auth';

interface PendingState {
  loadBusinessHours: boolean;
  upsertBusinessHours: boolean;
}

interface ErrorState {
  loadBusinessHours: Error | null;
  upsertBusinessHours: Error | null;
}

export const useBusinessHoursStore = defineStore('businessHours', () => {
  const businessHoursList = ref<BrandBusinessHours[]>([]);
  const pending = ref<PendingState>({
    loadBusinessHours: false,
    upsertBusinessHours: false,
  });
  const error = ref<ErrorState>({
    loadBusinessHours: null,
    upsertBusinessHours: null,
  });

  async function loadBusinessHours({ brandIds }: { brandIds: number[] | string }): Promise<void> {
    pending.value.loadBusinessHours = true;
    error.value.loadBusinessHours = null;
    try {
      const response = await AuthAPI.getBusinessHours({ brandIds });
      // TODO: OpenAPI spec marks BrandBusinessHours fields (brandId, timezone, etc.) as optional,
      // but the API always returns them. Update the spec to mark these as required.
      businessHoursList.value = response.data as BrandBusinessHours[];
    } catch (err) {
      error.value.loadBusinessHours = err as Error;
      throw err;
    } finally {
      pending.value.loadBusinessHours = false;
    }
  }

  async function upsertBusinessHours({
    brandId,
    hoursConfig,
    timezone,
  }: BrandBusinessHoursInput): Promise<void> {
    pending.value.upsertBusinessHours = true;
    error.value.upsertBusinessHours = null;
    try {
      const index = businessHoursList.value.findIndex((item) => item.brandId === brandId);
      const payload = { brandIds: [brandId], hoursConfig, timezone };
      let response;

      if (index > -1) {
        response = await AuthAPI.updateBusinessHours(payload);
      } else {
        response = await AuthAPI.createBusinessHours(payload);
      }
      if (response.data?.length) {
        // Upsert: Update existing record or append new record from response
        // TODO: Same BrandBusinessHours optional-fields mismatch as above — update OpenAPI spec.
        businessHoursList.value = unionBy(
          response.data as BrandBusinessHours[],
          businessHoursList.value,
          'brandId',
        );
      }
    } catch (err) {
      error.value.upsertBusinessHours = err as Error;
      throw err;
    } finally {
      pending.value.upsertBusinessHours = false;
    }
  }

  function getBrandBusinessHours(brandId: number): BrandBusinessHours | undefined {
    return businessHoursList.value.find((item) => item.brandId === brandId);
  }

  function hasBusinessHoursConfigured(brandId: number): boolean {
    const brandBusinessHours = getBrandBusinessHours(brandId);
    return brandBusinessHours !== null && brandBusinessHours !== undefined;
  }

  return {
    businessHoursList,
    pending,
    error,
    loadBusinessHours,
    upsertBusinessHours,
    getBrandBusinessHours,
    hasBusinessHoursConfigured,
  };
});
